/**
 * Todo lo que toca la Web Audio API, en un sitio.
 *
 * Hace dos cosas con el MISMO microfono y el mismo contexto:
 *
 * 1. El medidor de volumen, que es el indicador de que se esta capturando
 *    audio de verdad y no solo de que el boton esta pulsado.
 * 2. El PCM crudo que necesita el dictado en directo.
 *
 * Las dos son opcionales y fallan por separado: si no hay Web Audio, se graba
 * igual y el medidor se queda plano; si no hay AudioWorklet, se graba igual y
 * el dictado se va al respaldo por lotes. Ninguna de las dos es motivo para
 * no poder dictar.
 */

import { CHAT_TEXTS as TEXTS } from "../texts.ts";

export interface PcmRequest {
  /** Frecuencia exacta que pide el servidor. Si el navegador no la da, no
   *  hay PCM: remuestrear aqui es trabajo de verdad y el respaldo ya cubre
   *  este caso. */
  readonly sampleRate: number;
  /** Muestras por trozo. A 24 kHz, 2400 son 100 ms. */
  readonly frameSize: number;
  readonly onChunk: (chunk: ArrayBuffer) => void;
}

export interface AudioCaptureOptions {
  readonly onLevel: (level: number) => void;
  /** Solo para el dictado en directo. Sin esto, solo hay medidor. */
  readonly pcm?: PcmRequest;
}

export interface AudioCapture {
  /** `null` si no se pidio PCM o si se pudo montar; si no, el motivo. */
  readonly pcmError: string | null;
  stop: () => void;
}

/** Refrescar el medidor a 60 fps solo gasta renders; a 12 ya se ve vivo. */
const LEVEL_INTERVAL_MS = 80;

/**
 * El procesador que corre en el hilo de audio.
 *
 * Va como texto y se carga desde un Blob en vez de como archivo aparte porque
 * `addModule` pide una URL: asi no depende de como el empaquetador trate los
 * worklets, que es justo el tipo de cosa que funciona en desarrollo y se cae
 * en el build de produccion.
 *
 * Acumula hasta completar un trozo y lo manda TRANSFIRIENDO el buffer, no
 * copiandolo: esto corre en el hilo de audio y una copia por trama es lo que
 * acaba provocando cortes.
 */
const PCM_PROCESSOR = `
class PcmProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    this.frameSize = options.processorOptions.frameSize;
    this.samples = new Int16Array(this.frameSize);
    this.filled = 0;
  }

  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (channel === undefined) return true;

    for (let i = 0; i < channel.length; i += 1) {
      const sample = Math.max(-1, Math.min(1, channel[i]));
      this.samples[this.filled] = sample * 32767;
      this.filled += 1;

      if (this.filled === this.frameSize) {
        const chunk = this.samples.slice(0);
        this.port.postMessage(chunk.buffer, [chunk.buffer]);
        this.filled = 0;
      }
    }

    return true;
  }
}

registerProcessor("pcm-capture", PcmProcessor);
`;

/**
 * La firma de `startAudioCapture`, para poder sustituirla.
 *
 * Web Audio no existe fuera de un navegador de verdad, asi que sin este hueco
 * el dictado en directo no se podria probar sin levantar un navegador entero.
 */
export type AudioCaptureStarter = (
  stream: MediaStream,
  options: AudioCaptureOptions,
) => Promise<AudioCapture>;

export const startAudioCapture: AudioCaptureStarter = async (
  stream: MediaStream,
  options: AudioCaptureOptions,
): Promise<AudioCapture> => {
  const AudioContextCtor = (
    globalThis as { AudioContext?: typeof AudioContext }
  ).AudioContext;

  if (AudioContextCtor === undefined) {
    return { pcmError: "Este navegador no tiene Web Audio.", stop: noop };
  }

  let context: AudioContext;
  try {
    context = new AudioContextCtor(
      options.pcm === undefined ? {} : { sampleRate: options.pcm.sampleRate },
    );
  } catch {
    return { pcmError: "No se pudo abrir el audio del navegador.", stop: noop };
  }

  const stopped: (() => void)[] = [
    () => {
      void context.close().catch(() => {
        // Cerrar un contexto ya cerrado tira; no hay nada que hacer.
      });
    },
  ];
  const stop = (): void => {
    for (const undo of stopped.splice(0)) undo();
  };

  let source: MediaStreamAudioSourceNode;
  try {
    source = context.createMediaStreamSource(stream);
  } catch {
    stop();
    return { pcmError: TEXTS.voice.micPlug, stop: noop };
  }

  startMeter(context, source, options.onLevel, stopped);

  if (options.pcm === undefined) return { pcmError: null, stop };

  const pcmError = await startPcm(context, source, options.pcm, stopped);
  return { pcmError, stop };
};

const startMeter = (
  context: AudioContext,
  source: MediaStreamAudioSourceNode,
  onLevel: (level: number) => void,
  stopped: (() => void)[],
): void => {
  try {
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);

    const samples = new Uint8Array(analyser.frequencyBinCount);
    let frame: number | null = null;
    let last = 0;

    const tick = (now: number): void => {
      frame = requestAnimationFrame(tick);
      if (now - last < LEVEL_INTERVAL_MS) return;
      last = now;

      analyser.getByteTimeDomainData(samples);
      let sum = 0;
      for (const sample of samples) {
        const centered = (sample - 128) / 128;
        sum += centered * centered;
      }
      // El RMS de una voz normal no pasa de 0.25, así que se escala para que
      // la barra use todo su recorrido, y se tapa en 1.
      onLevel(Math.min(1, Math.sqrt(sum / samples.length) * 4));
    };

    frame = requestAnimationFrame(tick);
    stopped.push(() => {
      if (frame !== null) cancelAnimationFrame(frame);
      source.disconnect(analyser);
    });
  } catch {
    // Sin medidor se dicta igual. No se propaga.
  }
};

/** Devuelve el motivo por el que no hay PCM, o `null` si lo hay. */
const startPcm = async (
  context: AudioContext,
  source: MediaStreamAudioSourceNode,
  pcm: PcmRequest,
  stopped: (() => void)[],
): Promise<string | null> => {
  // El navegador puede ignorar la frecuencia pedida y dar la del dispositivo.
  // Mandar 48 kHz diciendo que son 24 produce audio al doble de velocidad: se
  // oye como una ardilla y transcribe basura. Mejor no haber PCM.
  if (context.sampleRate !== pcm.sampleRate) {
    return `El navegador graba a ${String(context.sampleRate)} Hz y el dictado en directo necesita ${String(pcm.sampleRate)} Hz.`;
  }

  const worklet = (context as { audioWorklet?: AudioWorklet }).audioWorklet;
  if (worklet === undefined) {
    return "Este navegador no puede procesar el audio en directo.";
  }

  const url = URL.createObjectURL(
    new Blob([PCM_PROCESSOR], { type: "text/javascript" }),
  );
  try {
    await worklet.addModule(url);
  } catch {
    return "No se pudo preparar el audio para el dictado en directo.";
  } finally {
    URL.revokeObjectURL(url);
  }

  try {
    const node = new AudioWorkletNode(context, "pcm-capture", {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      processorOptions: { frameSize: pcm.frameSize },
    });

    node.port.onmessage = (event: MessageEvent) => {
      if (event.data instanceof ArrayBuffer) pcm.onChunk(event.data);
    };

    source.connect(node);
    stopped.push(() => {
      node.port.onmessage = null;
      source.disconnect(node);
      node.disconnect();
    });
    return null;
  } catch {
    return "No se pudo capturar el audio para el dictado en directo.";
  }
};

const noop = (): void => {
  // Captura que no llego a montarse: pararla no tiene que hacer nada.
};
